// components/NamePrompt.tsx — post-CharacterSetup step that lets the player
// give their new story a name (or skip and use the scenario's own name).
//
// Lives in the new-story flow:
//
//   ScenarioPicker → CharacterSetup → NamePrompt → game
//
// The story already exists on the server at this point (POST /api/stories
// is fired immediately on scenario pick so the rest of the boot path has
// a real id to work with). When the user submits, the parent calls
// PATCH /api/stories/:id with the resolved name; on failure the story
// keeps whatever placeholder name it was created with — never blocks the
// player from getting into the game.
//
// Rendered inside the in-game ScenarioThemeWrapper so the page inherits
// the scenario's CSS custom properties (--accent, --paper-edge, --ink,
// etc.). The styles below pick up those vars and fall through to the
// Ember Hollow defaults when the wrapper isn't in scope (defensive).
//
// All styles inline — additive-only constraint, no styles.ts edits.

import {
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { styles } from "../styles";

export interface NamePromptProps {
  /**
   * Fallback name when the user submits blank or hits "Skip". Typically the
   * scenario's display name (e.g. "Ember Hollow", "Verity Drift").
   */
  scenarioName: string;
  /**
   * Always invoked with a non-empty resolved name — either the trimmed
   * input value, or `scenarioName` when the input is blank / skipped.
   */
  onComplete: (name: string) => void;
  /**
   * Optional escape hatch. When provided, renders an extra muted link the
   * user can click to abandon the new-story flow entirely without naming.
   * Today's caller doesn't pass this — Skip is wired to `onComplete` with
   * the scenario name. Kept for future flows that may need it.
   */
  onCancel?: () => void;
}

export default function NamePrompt({
  scenarioName,
  onComplete,
  onCancel,
}: NamePromptProps) {
  const { t } = useTranslation(["setup", "common"]);
  const [value, setValue] = useState("");
  const submittedRef = useRef(false);

  function resolve(): string {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : scenarioName;
  }

  function submit() {
    if (submittedRef.current) return; // guard against Enter + click double-fire
    submittedRef.current = true;
    // resolve() returns the trimmed input or falls back to scenarioName.
    // Begin therefore covers both "I want a custom name" (typed input) and
    // "Use the default" (blank) — no separate Skip affordance needed.
    onComplete(resolve());
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  }

  return (
    <div style={styles.loginShell}>
      <div style={styles.grain} />
      <div style={styles.loginCard}>
        <h1 style={titleStyle}>{t("setup:namePrompt.title")}</h1>
        <div style={subtitleStyle}>{t("setup:namePrompt.subtitle")}</div>

        <input
          autoFocus
          type="text"
          autoCapitalize="words"
          autoCorrect="off"
          value={value}
          onChange={(e) => setValue(e.target.value.slice(0, 120))}
          onKeyDown={onKeyDown}
          maxLength={120}
          placeholder={scenarioName}
          style={inputStyle}
          aria-label={t("setup:namePrompt.ariaLabel")}
        />

        <button
          type="button"
          style={{ ...styles.loginPrimary, marginTop: 22 }}
          onClick={submit}
        >
          {t("setup:namePrompt.beginCta")}
        </button>

        {onCancel && (
          <div style={styles.loginRow}>
            <button
              type="button"
              style={styles.loginLink}
              onClick={onCancel}
            >
              {t("common:buttons.cancel")}
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes flicker { 0%,100%{opacity:1} 50%{opacity:0.92} }
        @keyframes fadein { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}

// ── Styles (inline — see header) ────────────────────────────────────────
// Mirror CharacterSetup's visual language: IM Fell English title, muted
// monospace subtitle, italic-serif input. CSS vars come from
// ScenarioThemeWrapper; the rgba fallbacks reproduce Ember Hollow.

const titleStyle: CSSProperties = {
  margin: 0,
  fontFamily: "'IM Fell English', 'Cormorant Garamond', Georgia, serif",
  fontStyle: "italic",
  fontWeight: 400,
  fontSize: "2rem",
  color: "var(--ink, #e8c896)",
  letterSpacing: "0.02em",
  textAlign: "center",
  textShadow: "0 0 24px rgba(0,0,0,0.4)",
  lineHeight: 1.2,
};

const subtitleStyle: CSSProperties = {
  marginTop: 6,
  marginBottom: 26,
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.74rem",
  letterSpacing: "0.1em",
  color: "var(--ink-muted, #8a7355)",
  textTransform: "lowercase",
  textAlign: "center",
};

const inputStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  background: "transparent",
  border: "1px solid var(--paper-edge, rgba(212,165,116,0.28))",
  color: "var(--ink, #e8d9b8)",
  fontFamily:
    "var(--font-body, 'Cormorant Garamond', 'EB Garamond', Georgia, serif)",
  fontStyle: "italic",
  fontSize: "1.05rem",
  padding: "12px 14px",
  outline: "none",
  borderRadius: 2,
  textAlign: "center",
  transition:
    "background 0.25s ease-out, border-color 0.25s ease-out, color 0.25s ease-out",
};
